/**
 * **A04 · The incident card and the lost-child card**, **A05 · the radio card** and **A07 · the
 * gate clicker sheet** (Stream A, Paper.dc.html): what the field app records, on paper, for the
 * night it does not open, the card clipped to every radio, and the gate's hourly count. Every field
 * on the two cards is a label and a line to write on; the channel and Dispatch's number come from
 * the year's radio plan (Festival Ops), and what the plan does not hold prints NO DATA, never the
 * drawing's sample. The phrases and the lightning rule are the festival's procedure, as drawn.
 */
import { festivalDayWords } from './count_papers.js';
import { NO_DATA, type PaperPage } from './paper_page.js';

/** The year's radio plan, as Festival Ops answers it; null when there is none. */
export interface RadioPlan {
  channels: { channel: string; use: string }[];
  callSigns: { who: string; sign: string }[];
  /** The channel incidents, lost children and medical go on. */
  securityChannel: string | null;
  dispatchPhone: string | null;
}

/** A04 · the incident card and the lost-child card, two 4 × 6 cards. */
export function fieldCards(plan: RadioPlan | null): PaperPage[] {
  const ch = plan?.securityChannel ?? NO_DATA;
  return [
    {
      size: 'card',
      title: 'Incident',
      sub: `Radio Channel ${ch} (Security) · Dispatch ${plan?.dispatchPhone ?? NO_DATA}`,
      qr: 'INC',
      blocks: [
        {
          head: '',
          cols: '70px 1fr',
          rows: [
            ['What', ''],
            ['Where', ''],
            ['When', ''],
            ['Who', ''],
            ['Reported by', ''],
            ['Injury?', 'Y / N  ·  First aid  ·  Transport  ·  Refused'],
            ['Police / fire?', 'Y / N  ·  time called'],
            ['Resolved by', ''],
            ['Resolved at', ''],
          ],
        },
      ],
      sign: ['Reported by', 'Chair or Dispatch'],
      foot: 'Incident card · enter in CIDER by Sunday night',
    },
    {
      size: 'card',
      title: 'Lost child',
      sub: `Say it on Channel ${ch} first. Then fill this in.`,
      qr: 'CHILD',
      blocks: [
        {
          head: '',
          cols: '80px 1fr',
          rows: [
            ['Child’s name', ''],
            ['Age · height', ''],
            ['Wearing', ''],
            ['Last seen', 'where · when'],
            ['Parent / guardian', ''],
            ['Their phone', ''],
            ['Found at · by', ''],
            ['Reunited at', 'time · who handed over · ID checked Y / N'],
          ],
        },
      ],
      sign: ['Taken by', 'Dispatch'],
      foot: 'Lost-child card · stays with Dispatch · never posted',
    },
  ];
}

/** A05 · the radio card, front and back (6 × 4, laminated). */
export function radioCard(year: number, plan: RadioPlan | null): PaperPage[] {
  const y = String(year);
  return [
    {
      size: 'card-landscape',
      title: `Radio card · ${y} · front`,
      sub: 'Say your area, then who you want. Wait for the answer.',
      blocks: [
        {
          head: 'CHANNELS',
          cols: '26px 1fr',
          rows: plan?.channels.length
            ? plan.channels.map((c) => [c.channel, c.use])
            : [['', NO_DATA]],
        },
        {
          head: 'CALL SIGNS',
          cols: '1fr 1fr',
          rows: plan?.callSigns.length
            ? plan.callSigns.map((c) => [c.who, c.sign])
            : [[NO_DATA, '']],
        },
      ],
      foot: 'Radio card · front · return with the radio',
    },
    {
      size: 'card-landscape',
      title: `Radio card · ${y} · back`,
      sub: 'The phrases and the lightning rule.',
      blocks: [
        {
          head: 'THE PHRASES',
          cols: '1fr 1fr',
          rows: [
            ['“Dispatch, Pub — go ahead?”', '“Copy” · “Say again” · “Stand by”'],
            [
              `“Medical to …” — Channel ${plan?.securityChannel ?? NO_DATA}, then stay on`,
              '“All stations, hold” — stop and listen',
            ],
          ],
        },
        {
          head: 'LIGHTNING',
          cols: '1fr',
          rows: [
            [
              'Within 8 miles: Dispatch calls the hold on all channels. Stop, close the tent, send people to the hall. Do not transmit except an emergency. Resume only when Dispatch says so.',
            ],
          ],
        },
      ],
      foot: 'Radio card · back',
    },
  ];
}

/**
 * A07 · the gate clicker sheet: one sheet per gate per day, hourly readings and initials; the
 * attendance figure is the sum of the sheets. The gates are Festival Ops's (`GET /ops/gates`);
 * CIDER keeps no festival hours, so the hour column is a line to write on, like the readings.
 */
export function gateSheets(day: string, gates: string[] | null): PaperPage[] {
  const words = festivalDayWords(day);
  const short = words.slice(0, 3).toUpperCase();
  return (gates?.length ? gates : [null]).map((g) => {
    const gate = g ?? `Gate ${NO_DATA}`;
    const first = (g ?? '').split(' ')[0]?.toUpperCase() ?? '';
    return {
      title: 'Gate count',
      sub: `${gate} · ${words} · clicker ____`,
      qr: `GATE ${first || NO_DATA} ${short}`,
      blocks: [
        {
          head: 'BY THE HOUR',
          cols: '110px 1fr 80px 80px',
          rows: [
            ['HOUR', 'CLICKER READS', 'THIS HOUR', 'INITIALS'],
            ...Array.from({ length: 13 }, () => ['', '', '', '']),
          ],
        },
        {
          head: '',
          cols: '1fr 120px',
          rows: [
            [`Today · ${gate.toLowerCase()}`, ''],
            ['Reset the clicker to 0 at open · note the reading if not', 'Opened at: '],
          ],
        },
      ],
      sign: ['Opened by', 'Closed by'],
      foot: 'Gate clicker sheet · keyed into Festival Ops at close',
    };
  });
}
